import { useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 暗网91安全指南',
  description:
    '暗网91安全指南团队介绍、品牌信誉与联系方式。我们致力于为中文用户提供专业的暗网安全知识与隐私保护指导。',
  url: 'https://www.anwang91.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '暗网91安全指南',
    url: 'https://www.anwang91.com',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '暗网91安全指南',
  url: 'https://www.anwang91.com',
  description:
    '暗网91安全指南是专注于暗网安全知识与隐私保护的中文教育平台，为中文用户提供暗网访问教程、安全指南和深度资讯。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: ['Chinese'],
  },
};

const milestones = [
  { year: '2024', title: '平台创立', description: '暗网91安全指南正式上线，开始为中文用户提供暗网安全知识内容。' },
  { year: '2025', title: '内容扩展', description: '拓展内容覆盖面，新增隐私保护、深度评测和最新动态板块，月访问量突破10万。' },
  { year: '2026', title: '生态建设', description: '建立完整的暗网安全知识体系，成为中文暗网安全领域的权威参考平台。' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '安全第一',
    description: '所有内容以用户安全为核心出发点，提供经过验证的防护策略和安全建议。',
  },
  {
    icon: 'ri-book-open-line',
    title: '知识普及',
    description: '以通俗易懂的方式传播暗网安全知识，降低学习门槛，帮助更多用户建立安全意识。',
  },
  {
    icon: 'ri-scales-line',
    title: '合规导向',
    description: '强调合法合规使用，引导用户在遵守法律法规的前提下了解暗网技术和安全知识。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户至上',
    description: '持续关注用户需求，提供及时、准确、实用的暗网安全信息和防护建议。',
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9q2avnnahb7382ov4ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (
        response.ok &&
        parsed?.code === 'OK'
      ) {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(
          typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。'
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们"
        description="暗网91安全指南团队介绍、品牌信誉与联系方式。我们致力于为中文用户提供专业的暗网安全知识与隐私保护指导。"
        keywords="暗网91,关于我们,暗网安全团队,联系方式,品牌介绍"
        canonicalUrl="https://www.anwang91.com/about"
        schemaJson={{ ...aboutSchema, ...orgSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mt-4 mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-2xl">
              暗网91安全指南是专注于暗网安全知识与隐私保护的中文教育平台，致力于帮助中文用户安全、合规地了解深网世界。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                  我们的使命
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  在数字化时代，暗网既是技术前沿，也是安全重灾区。我们创立暗网91安全指南，旨在为中文互联网用户提供一个可靠、专业的暗网安全知识平台，帮助大家理解暗网的运作机制，掌握必要的安全防护技能，在探索深网世界的同时保护好自己的隐私和数据安全。
                </p>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                  我们的愿景
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为华语世界最权威的暗网安全知识平台，让每一位对暗网感兴趣的中文用户都能找到可信赖的安全指南和实用教程，在安全合规的前提下拓展数字世界的认知边界。
                </p>
              </div>
              <div className="relative rounded-lg overflow-hidden h-64 md:h-full">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20digital%20security%20team%20workspace%20with%20multiple%20monitors%20displaying%20cybersecurity%20dashboards%2C%20warm%20amber%20and%20green%20ambient%20lighting%2C%20professional%20yet%20warm%20atmosphere%2C%20minimalist%20office%20design&width=800&height=600&seq=about-brand&orientation=portrait"
                  alt="暗网91安全指南团队"
                  title="暗网91安全指南 - 品牌介绍"
                  className="w-full h-full object-cover object-top rounded-lg"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              发展历程
            </h2>
            <p className="text-sm text-foreground-500 mb-10">从创立到成长的每一步</p>
            <div className="relative">
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-primary-200"></div>
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className="relative flex items-start gap-6 pl-12 md:pl-16">
                    <div className="absolute left-2 md:left-4 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50"></div>
                    <div>
                      <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-2">
                        {milestone.year}
                      </span>
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-1">
                        {milestone.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              品牌信誉
            </h2>
            <p className="text-sm text-foreground-500 mb-10">我们的核心价值观</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-5 rounded-lg border border-background-200/70 bg-background-50"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className={`${value.icon} text-primary-600 text-lg`}></i>
                  </span>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="text-3xl font-heading font-bold text-primary-600">120+</span>
                <p className="text-sm text-foreground-500 mt-1">原创文章</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="text-3xl font-heading font-bold text-accent-600">50万+</span>
                <p className="text-sm text-foreground-500 mt-1">月度读者</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="text-3xl font-heading font-bold text-primary-600">99.8%</span>
                <p className="text-sm text-foreground-500 mt-1">服务可用率</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 mb-10 text-center">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                  电子邮件
                </h3>
                <p className="text-xs text-foreground-500">contact@anwang91.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100">
                  <i className="ri-message-3-line text-accent-600 text-lg"></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                  在线留言
                </h3>
                <p className="text-xs text-foreground-500">填写下方表单</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                  <i className="ri-time-line text-primary-600 text-lg"></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                  响应时间
                </h3>
                <p className="text-xs text-foreground-500">24小时内回复</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8"
            >
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                在线留言
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-200 bg-white focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-200 bg-white focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="contact-subject"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  主题 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-200 bg-white focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200"
                  placeholder="请输入留言主题"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-200 bg-white focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700">
                  <i className="ri-check-line mr-1.5"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-4 p-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="px-6 py-2.5 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm whitespace-nowrap"
              >
                {formStatus === 'loading' ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center inline-block mr-1.5">
                      <i className="ri-loader-4-line animate-spin"></i>
                    </span>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-1.5"></i>
                    发送留言
                  </>
                )}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}